/*
 * HAT-1441: the office locks a collection date before it sends that day's Masav file to the bank, and
 * the bank standing-orders board stops taking changes to the orders that collect on it (the office,
 * 21.10.2026: «לפני שמשדרים קובץ של 20 לחודש - ננעל את ה-20 לחודש ואז נשדר»). The lock ends by itself
 * once the day has passed (BankCollectionLockService on the server).
 */

export const BANK_COLLECTION_LOCKS_URL = 'management/bank-collection-locks';

/** A lock in force, as the server sends it. */
export interface BankCollectionLockDtoIn {
  /** yyyy-MM-dd */
  collectionDate: string;
  lockedBy: string | null;
  /** UTC */
  lockedAt: string;
}

/** The days of the month the office's bank orders collect on - all 3,502 live orders in APT (20.10.2026). */
export const BILLING_DAYS = [1, 5, 10, 15, 20, 25] as const;

export const toIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const fromIsoDate = (text: string): Date => {
  const [y, m, d] = text.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * The next collection on each billing day, after today and earliest first: the dates whose file the
 * office has yet to send. Today's is not among them - its file is already at the bank.
 */
export function upcomingCollectionDates(now: Date = new Date()): Date[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return BILLING_DAYS
    .map((day) => {
      const thisMonth = new Date(today.getFullYear(), today.getMonth(), day);
      return thisMonth > today ? thisMonth : new Date(today.getFullYear(), today.getMonth() + 1, day);
    })
    .sort((a, b) => a.getTime() - b.getTime());
}

export interface CollectionRow {
  /** yyyy-MM-dd */
  date: string;
  lock: BankCollectionLockDtoIn | null;
}

/** One row per coming collection, with its lock if there is one - and any other date the office locked. */
export function collectionRows(locks: readonly BankCollectionLockDtoIn[] | undefined, now: Date = new Date()): CollectionRow[] {
  const byDate = new Map((locks ?? []).map((lock) => [lock.collectionDate, lock]));
  const dates = new Set([...upcomingCollectionDates(now).map(toIsoDate), ...byDate.keys()]);
  return [...dates].sort().map((date) => ({ date, lock: byDate.get(date) ?? null }));
}
